// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { useMemo } from "react";

import { ExtractedRecord, TargetSchema } from "@/types";
import { TransformationTemplate } from "@/types/transformationTemplate";
import {
  AdvisoryCondition,
  CriticalFieldEntry,
  getCriticalCoverage,
} from "../components/document-review/criticalFields";
import { getRecordData } from "../utils/recordUtils";

type RecordsBySchema = Map<TargetSchema, ExtractedRecord[]>;

export interface CriticalFieldAdvisoryEntry {
  schema: TargetSchema;
  entry: CriticalFieldEntry;
}

/**
 * Document-level context the advisory hook needs to evaluate conditional
 * entries. All fields are optional — the hook degrades to "fire all
 * conditional entries" when context is unavailable, so the surface is at
 * worst noisier rather than silently dropping signal.
 */
export interface CriticalFieldAdvisoryContext {
  /** Extracted records from the current document. Used by `extracted-record-has-data` conditions. */
  extractedRecords?: ExtractedRecord[];
  /** True when the current policy has a family deductible. Used by `family-deductible-policy` conditions. */
  hasFamilyDeductiblePolicy?: boolean;
}

/**
 * Returns true when the entry should be surfaced for the current document,
 * based on its (optional) condition and the supplied context. Unconditional
 * entries always pass.
 */
const shouldSurface = (
  entry: CriticalFieldEntry,
  context: CriticalFieldAdvisoryContext,
  recordsBySchema: RecordsBySchema
): boolean => {
  if (!entry.condition) return true;
  return evaluateCondition(entry.condition, context, recordsBySchema);
};

const evaluateCondition = (
  condition: AdvisoryCondition,
  context: CriticalFieldAdvisoryContext,
  recordsBySchema: RecordsBySchema
): boolean => {
  switch (condition.type) {
    case "family-deductible-policy":
      // Default to "fire" when context is unknown — better to show the
      // advisory than silently drop it because we can't tell.
      return context.hasFamilyDeductiblePolicy ?? true;
    case "extracted-record-has-data": {
      const records = recordsBySchema.get(condition.schema) ?? [];
      if (records.length === 0) return false;
      return records.some((record) => {
        const value = getRecordData(record)[condition.field];
        if (value === null || value === undefined || value === "") return false;
        // Numeric zero counts as "no data" — carriers commonly emit 0 to mean
        // "not pended", "not denied", etc., so 0 shouldn't fire the condition.
        // Don't apply that to other types: boolean `false` is meaningful data
        // (`false !== 0` is `true` in JS, which would have made every
        // boolean-targeted condition fire unconditionally).
        if (typeof value === "number" && value === 0) return false;
        return true;
      });
    }
  }
};

const indexRecordsBySchema = (records: ExtractedRecord[] | undefined): RecordsBySchema => {
  const bySchema = new Map<TargetSchema, ExtractedRecord[]>();
  if (!records) return bySchema;
  for (const record of records) {
    if (!record.extracted_fields) continue;
    const seenSchemas = new Set<TargetSchema>();
    for (const field of record.extracted_fields) {
      if (seenSchemas.has(field.target_schema)) continue;
      seenSchemas.add(field.target_schema);
      const list = bySchema.get(field.target_schema) ?? [];
      list.push(record);
      bySchema.set(field.target_schema, list);
    }
  }
  return bySchema;
};

/**
 * Pure helper: returns the unmapped critical slots for a single schema, with
 * conditional entries filtered against the supplied document context. Lets the
 * field-mapping dialog compute its in-flight advisory list against live edited
 * mappings (which never round-trip through `template.field_mappings`) without
 * duplicating the hook's filtering logic.
 */
export const getUnmappedCriticalSlots = (
  schema: TargetSchema,
  mappedFields: Iterable<string>,
  context: CriticalFieldAdvisoryContext = {}
): CriticalFieldEntry[] => {
  const { unmappedSlots } = getCriticalCoverage(schema, mappedFields);
  const indexed = indexRecordsBySchema(context.extractedRecords);
  return unmappedSlots.filter((entry) => shouldSurface(entry, context, indexed));
};

/**
 * Derives the per-schema list of critical fields that the current template
 * does NOT map AND whose conditions hold for this document. Advisory only —
 * never feeds approval gating.
 *
 * Composite alternatives collapse into a single coverage slot, so a template
 * mapping `claimant_first_name` + `claimant_last_name` does not surface
 * `claimant_name` as missing.
 *
 * Conditional entries (status-conditional reasons, family-unit subscriber
 * id) are filtered out when their condition doesn't hold against the current
 * document — keeps the advisory list focused on what's actionable for THIS
 * document instead of every theoretically-relevant gap.
 */
export const useCriticalFieldAdvisory = (
  template: TransformationTemplate | undefined,
  availableSchemas: TargetSchema[],
  context: CriticalFieldAdvisoryContext = {}
): CriticalFieldAdvisoryEntry[] => {
  return useMemo(() => {
    if (!template?.field_mappings || availableSchemas.length === 0) return [];

    const mappedBySchema: Partial<Record<TargetSchema, Set<string>>> = {};
    for (const fm of template.field_mappings) {
      const set = mappedBySchema[fm.target_schema] ?? new Set<string>();
      set.add(fm.target_field);
      mappedBySchema[fm.target_schema] = set;
    }

    const recordsBySchema = indexRecordsBySchema(context.extractedRecords);

    const advisories: CriticalFieldAdvisoryEntry[] = [];
    for (const schema of availableSchemas) {
      const mapped = mappedBySchema[schema] ?? new Set<string>();
      const { unmappedSlots } = getCriticalCoverage(schema, mapped);
      for (const entry of unmappedSlots) {
        if (!shouldSurface(entry, context, recordsBySchema)) continue;
        advisories.push({ schema, entry });
      }
    }
    return advisories;
  }, [template, availableSchemas, context]);
};
